import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Eye,
  Users,
  GraduationCap,
  UserPlus,
  Store,
  Shield,
  Baby,
  DollarSign,
  Trophy,
  FileText,
  Mail,
  Phone,
  Package,
  Stethoscope,
  CreditCard,
  AlertCircle,
  Calendar,
  Settings,
  Save,
  ExternalLink,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Validate SCC Membership Number format
 * 
 * Valid patterns (internal reference - not shown to users):
 * 
 * BASIC FORMAT: [Letter][4 digits]
 * Example: P0354
 * 
 * JUNIOR FORMAT: J + [Letter][4 digits]
 * Example: JP0354 (members under 26 years old)
 * 
 * FAMILY MEMBER FORMAT: [Basic] + [Family Suffix]
 * Suffixes:
 *   - WFM  = Wife Family Member (e.g., P0354WFM)
 *   - HFM  = Husband Family Member (e.g., P0354HFM)
 *   - D1FM = Daughter 1 Family Member (e.g., P0354D1FM)
 *   - D2FM = Daughter 2 Family Member, etc.
 *   - MFM  = Mother Family Member (e.g., P0354MFM)
 *   - FFM  = Father Family Member (e.g., P0354FFM)
 * 
 * HONORARY FORMAT: H + [Letter][4 digits]
 * Example: HP0354 (embassy personnel with honorary membership)
 * 
 * NOTE: There may be other formats not listed here. Validation is intentionally
 * flexible to allow for unknown formats while flagging clearly invalid entries.
 * 
 * Returns: { valid: boolean, warning: string | null }
 */
const validateMembershipNumber = (membershipNumber) => {
  if (!membershipNumber) {
    return { valid: false, warning: "No membership number provided" };
  }
  
  const normalized = membershipNumber.trim().toUpperCase();
  
  // Valid patterns - intentionally flexible to allow for unknown formats
  const patterns = [
    /^[A-Z]\d{4}$/,              // Basic: P0354
    /^[A-Z]\d{4}[A-Z0-9]*FM$/,   // Family member: P0354WFM, P0354HFM, P0354D1FM, P0354MFM, P0354FFM, etc.
    /^H[A-Z]\d{4}$/,             // Honorary: HP0354
    /^J[A-Z]\d{4}$/,             // Junior (under 26): JP0354
    /^[A-Z]{1,2}\d{4}[A-Z]*$/,   // Catch other potential formats: 1-2 letters + 4 digits + optional suffix
  ];
  
  const isValid = patterns.some(pattern => pattern.test(normalized));
  
  if (!isValid) {
    return { 
      valid: false, 
      warning: "Membership number format may not be valid. Please verify with SCC records - non-member pricing will apply if invalid."
    };
  }
  
  return { valid: true, warning: null };
};

/**
 * Component to display membership validation status
 */
const MembershipValidation = ({ membershipNumber, showDetails = false }) => {
  const validation = validateMembershipNumber(membershipNumber);
  
  if (!membershipNumber) {
    return (
      <div className="flex flex-col gap-1">
        <Badge className="bg-red-500/20 text-red-400 text-xs">⚠️ No membership #</Badge>
        {showDetails && (
          <span className="text-red-400 text-xs">Please verify - non-member pricing applies if not valid SCC member</span>
        )}
      </div>
    );
  }
  
  if (!validation.valid) {
    return (
      <div className="flex flex-col gap-1">
        <Badge className="bg-amber-500/20 text-amber-400 text-xs font-mono">⚠️ #{membershipNumber}</Badge>
        {showDetails && (
          <span className="text-amber-400 text-xs">{validation.warning}</span>
        )}
      </div>
    );
  }
  
  // Valid format
  return (
    <div className="flex flex-col gap-1">
      <Badge className="bg-blue-500/20 text-blue-400 text-xs font-mono">#{membershipNumber}</Badge>
    </div>
  );
};

// Membership validation flag — quick resolution badge with inline action buttons.
// Replaces the static rose badge introduced Feb 2026. One click approves the
// flagged member as SCC-verified; the other converts them to non-member.
const FlagResolutionBadge = ({ member, onResolved }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const raw = member.membership_validation_flag?.raw_number_submitted || member.membership_number || "";
  const reason = member.membership_validation_flag?.validator_error || "";

  const resolve = async (action) => {
    const confirmMsg = action === "approve_scc"
      ? `Confirm: approve ${member.email || member.name} as a verified SCC member with number "${raw}"? You should have eyeballed the membership card or called the member first.`
      : `Confirm: convert ${member.email || member.name} to non-member? Their submitted number "${raw}" will be cleared.`;
    if (!window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/membership-flags/${member.id}/resolve`,
        { action },
        { withCredentials: true },
      );
      toast.success(action === "approve_scc"
        ? "Approved as SCC member"
        : "Converted to non-member");
      setOpen(false);
      if (onResolved) onResolved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Resolution failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative inline-block" data-testid={`flag-badge-${member.id}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-rose-500/20 text-rose-300 border border-rose-500/40 text-xs hover:bg-rose-500/30 transition-colors"
        title={`Submitted "${raw}". ${reason}. Click to resolve.`}
        data-testid={`flag-badge-toggle-${member.id}`}
      >
        ⚠ Format flagged
      </button>
      {open && (
        <div
          className="absolute z-30 mt-1 left-0 w-72 bg-slate-900 border border-rose-500/40 rounded shadow-lg p-3 space-y-2 text-xs text-slate-200"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="border-b border-slate-700 pb-2 mb-2">
            <div className="text-slate-400">Submitted:</div>
            <div className="font-mono text-rose-300 break-all">{raw || "—"}</div>
          </div>
          <Button
            size="sm"
            onClick={() => resolve("approve_scc")}
            disabled={busy}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
            data-testid={`flag-approve-${member.id}`}
          >
            Approve as SCC member
          </Button>
          <Button
            size="sm"
            onClick={() => resolve("reject_to_non_member")}
            disabled={busy}
            variant="outline"
            className="w-full border-slate-600 text-slate-200 hover:bg-slate-800 text-xs"
            data-testid={`flag-reject-${member.id}`}
          >
            Reject — convert to non-member
          </Button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="w-full text-slate-500 hover:text-slate-300 underline text-xs pt-1"
            data-testid={`flag-cancel-${member.id}`}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};



export default function AdminApprovals({ user, logout }) {
  const [searchParams, setSearchParams] = useSearchParams();
  
  const initialTab = searchParams.get("tab") || "academy";
  const initialSection = searchParams.get("section") || "";
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const [activeSection, setActiveSection] = useState(initialSection);
  
  // Academy data
  const [pendingRegistrations, setPendingRegistrations] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [coachApplications, setCoachApplications] = useState([]);
  const [academyEnquiries, setAcademyEnquiries] = useState([]);
  const [pendingMCs, setPendingMCs] = useState([]);
  const [unassignedPlayers, setUnassignedPlayers] = useState([]);
  const [academyTeams, setAcademyTeams] = useState([]);
  const [adhocBookings, setAdhocBookings] = useState([]);
  const [outstandingPayments, setOutstandingPayments] = useState([]);
  
  // Section data
  const [pendingMembers, setPendingMembers] = useState([]);
  const [joinRequests, setJoinRequests] = useState([]);
  const [merchantApplications, setMerchantApplications] = useState([]);
  const [membershipEnquiries, setMembershipEnquiries] = useState([]);
  const [nonFootballPayments, setNonFootballPayments] = useState([]);
  
  // Orders data
  const [orders, setOrders] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, item: null, type: null });
  
  // Member status override state
  const [memberOverride, setMemberOverride] = useState({ 
    editing: false, 
    memberType: '', 
    saving: false 
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (activeTab) params.set("tab", activeTab);
    if (activeSection) params.set("section", activeSection);
    setSearchParams(params, { replace: true });
  }, [activeTab, activeSection, setSearchParams]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        // Academy data
        axios.get(`${API}/academy/admin/pending-registrations`, { withCredentials: true }),
        axios.get(`${API}/payments/admin/pending-verifications`, { withCredentials: true }),
        axios.get(`${API}/payments/admin/all-transactions`, { withCredentials: true }),
        axios.get(`${API}/admin/coach-applications`, { withCredentials: true }),
        axios.get(`${API}/admin/academy-enquiries`, { withCredentials: true }),
        axios.get(`${API}/admin/pending-mc-approvals`, { withCredentials: true }),
        axios.get(`${API}/academy/admin/unassigned-players`, { withCredentials: true }),
        axios.get(`${API}/academy/teams`, { withCredentials: true }),
        axios.get(`${API}/academy/admin/outstanding-payments`, { withCredentials: true }),
        axios.get(`${API}/academy/admin/adhoc-bookings`, { withCredentials: true }), // NEW: Ad-hoc bookings
        // Section data
        axios.get(`${API}/admin/users?filter=pending`, { withCredentials: true }),
        axios.get(`${API}/admin/join-requests`, { withCredentials: true }),
        axios.get(`${API}/admin/merchant-applications`, { withCredentials: true }),
        axios.get(`${API}/admin/membership-enquiries`, { withCredentials: true }),
        axios.get(`${API}/admin/non-football-payment-proofs`, { withCredentials: true }),
        // Orders
        axios.get(`${API}/admin/orders`, { withCredentials: true }),
      ]);

      // Academy
      if (results[0].status === "fulfilled") setPendingRegistrations(results[0].value.data || []);
      if (results[1].status === "fulfilled") setPendingPayments(results[1].value.data || []);
      if (results[2].status === "fulfilled") setAllPayments(results[2].value.data || []);
      if (results[3].status === "fulfilled") setCoachApplications(results[3].value.data || []);
      if (results[4].status === "fulfilled") setAcademyEnquiries(results[4].value.data || []);
      if (results[5].status === "fulfilled") setPendingMCs(results[5].value.data || []);
      if (results[6].status === "fulfilled") setUnassignedPlayers(results[6].value.data || []);
      if (results[7].status === "fulfilled") setAcademyTeams(results[7].value.data || []);
      if (results[8].status === "fulfilled") setOutstandingPayments(results[8].value.data || []);
      if (results[9].status === "fulfilled") setAdhocBookings(results[9].value.data || []);
      
      // Section
      if (results[10].status === "fulfilled") {
        const users = results[10].value.data || [];
        setPendingMembers(users.filter(u => !u.is_authorized));
      }
      if (results[11].status === "fulfilled") setJoinRequests(results[11].value.data || []);
      if (results[12].status === "fulfilled") setMerchantApplications(results[12].value.data || []);
      if (results[13].status === "fulfilled") setMembershipEnquiries(results[13].value.data || []);
      if (results[14].status === "fulfilled") setNonFootballPayments(results[14].value.data || []);
      
      // Orders
      if (results[15].status === "fulfilled") setOrders(results[15].value.data || []);
      
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load approval data");
    } finally {
      setLoading(false);
    }
  };

  // ===== HANDLERS =====
  
  const handleAction = async (endpoint, successMsg) => {
    try {
      await axios.post(`${API}${endpoint}`, {}, { withCredentials: true });
      toast.success(successMsg);
      await fetchAllData();
      setConfirmDialog({ open: false, action: null, item: null, type: null });
    } catch (error) {
      toast.error("Action failed");
    } finally {
      setActionLoading(null);
    }
  };

  const [requestingProof, setRequestingProof] = useState(null);
  
  const handleRequestMembershipProof = async (registrationId) => {
    setRequestingProof(registrationId);
    try {
      await axios.post(`${API}/academy/admin/membership-cards/request-proof/${registrationId}`, {}, { withCredentials: true });
      toast.success("Verification request sent to parent");
      await fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send request");
    } finally {
      setRequestingProof(null);
    }
  };

  const handlePaymentVerify = async (transactionId, action) => {
    setActionLoading(transactionId);
    await handleAction(`/payments/paynow/verify/${transactionId}?action=${action}`, 
      action === "approve" ? "Payment approved!" : "Payment rejected.");
  };

  const [rejectionReason, setRejectionReason] = useState("");

  const handleRegistrationApprove = async (registrationId, action) => {
    setActionLoading(registrationId);
    if (action === "reject") {
      // Include rejection reason
      try {
        await axios.post(`${API}/academy/admin/reject-registration/${registrationId}`, 
          { reason: rejectionReason || "Your registration did not meet our requirements." },
          { withCredentials: true }
        );
        toast.success("Registration rejected. Parent has been notified.");
        setRejectionReason("");
        await fetchAllData();
        setConfirmDialog({ open: false, action: null, item: null, type: null });
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to reject registration");
      } finally {
        setActionLoading(null);
      }
    } else {
      await handleAction(`/academy/admin/approve-registration/${registrationId}`, "Registration approved!");
    }
  };

  const handleMemberAuthorize = async (userId, action) => {
    setActionLoading(userId);
    try {
      if (action === "approve") {
        await axios.post(`${API}/admin/authorize-user`, { user_id: userId }, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/reject-user`, { user_id: userId }, { withCredentials: true });
      }
      toast.success(action === "approve" ? "Member authorized!" : "Member rejected.");
      await fetchAllData();
      setConfirmDialog({ open: false, action: null, item: null, type: null });
    } catch (error) {
      toast.error("Failed to process member");
    } finally {
      setActionLoading(null);
    }
  };

  const handleJoinRequest = async (requestId, action) => {
    setActionLoading(requestId);
    await handleAction(`/teams/join-requests/${requestId}/${action}`, 
      action === "approve" ? "Join request approved!" : "Join request rejected.");
  };

  const handleMerchantApplication = async (applicationId, action) => {
    setActionLoading(applicationId);
    await handleAction(`/merchant-applications/${applicationId}/${action}`, 
      action === "approve" ? "Merchant approved!" : "Merchant rejected.");
  };

  const handleCoachApplication = async (applicationId, action) => {
    setActionLoading(applicationId);
    await handleAction(`/admin/coach-applications/${applicationId}/${action}`, 
      action === "approve" ? "Coach approved!" : "Coach rejected.");
  };

  const handleEnquiryContacted = async (enquiryId, type) => {
    setActionLoading(enquiryId);
    const endpoint = type === "academy" ? `/admin/academy-enquiries/${enquiryId}/contacted` : `/admin/membership-enquiries/${enquiryId}/contacted`;
    await handleAction(endpoint, "Marked as contacted");
  };

  const handleMCApproval = async (attendanceId, action) => {
    setActionLoading(attendanceId);
    await handleAction(`/admin/mc-approvals/${attendanceId}/${action}`, 
      action === "approve" ? "MC approved!" : "MC rejected.");
  };

  const handleOrderStatus = async (orderId, newStatus) => {
    setActionLoading(orderId);
    try {
      await axios.post(`${API}/admin/orders/${orderId}/update-status?status=${newStatus}`, {}, { withCredentials: true });
      toast.success(`Order status updated to ${newStatus}`);
      await fetchAllData();
    } catch (error) {
      toast.error("Failed to update order");
    } finally {
      setActionLoading(null);
    }
  };

  const handleMemberStatusOverride = async (registrationId, memberType) => {
    setMemberOverride(prev => ({ ...prev, saving: true }));
    try {
      const isSccMember = memberType === 'section' || memberType === 'scc_member';
      await axios.put(
        `${API}/academy/admin/registration/${registrationId}/member-status`,
        { 
          is_scc_member: isSccMember, 
          member_type: memberType,
          admin_note: `Admin override: changed to ${memberType}`
        },
        { withCredentials: true }
      );
      toast.success(`Member status updated to ${memberType.replace('_', ' ')}`);
      // Refresh the registration data
      await fetchAllData();
      // Update the selectedItem with new member status
      setSelectedItem(prev => prev ? {
        ...prev,
        is_scc_member: isSccMember,
        member_type: memberType,
        member_status_override: true
      } : null);
      setMemberOverride({ editing: false, memberType: '', saving: false });
    } catch (error) {
      console.error("Error updating member status:", error);
      toast.error("Failed to update member status");
      setMemberOverride(prev => ({ ...prev, saving: false }));
    }
  };

  const openConfirmDialog = (item, action, type) => {
    setConfirmDialog({ open: true, action, item, type });
  };

  // ===== HELPERS =====
  
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-SG", {
      month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit"
    });
  };

  const formatCurrency = (amount) => `$${parseFloat(amount || 0).toFixed(2)}`;

  const getStatusBadge = (status) => {
    const config = {
      pending_verification: { color: "bg-yellow-500/20 text-yellow-400 border-white/5", label: "Pending Verification" },
      pending: { color: "bg-orange-500/20 text-orange-400 border-orange-500/30", label: "Pending" },
      approved: { color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", label: "Approved" },
      paid: { color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", label: "Paid" },
      rejected: { color: "bg-red-500/20 text-red-400 border-red-500/30", label: "Rejected" },
      contacted: { color: "bg-blue-500/20 text-blue-400 border-blue-500/30", label: "Contacted" },
      confirmed: { color: "bg-blue-500/20 text-blue-400 border-blue-500/30", label: "Confirmed" },
      processing: { color: "bg-purple-500/20 text-purple-400 border-purple-500/30", label: "Processing" },
      shipped: { color: "bg-indigo-500/20 text-indigo-400 border-indigo-500/30", label: "Shipped" },
      delivered: { color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", label: "Delivered" },
    };
    const c = config[status] || { color: "bg-slate-800/50 text-white", label: status };
    return <Badge variant="outline" className={`${c.color} font-medium`}>{c.label}</Badge>;
  };

  // ===== COUNTS =====
  const pendingCoaches = coachApplications.filter(c => c.status === "pending").length;
  const pendingAcademyEnquiries = academyEnquiries.filter(e => e.status === "pending").length;
  const pendingOutstanding = outstandingPayments.filter(p => p.status !== "paid" && p.status !== "waived").length;
  const pendingAdhoc = adhocBookings.filter(b => b.payment_status === "pending").length;
  const academyCount = pendingRegistrations.length + pendingPayments.length + pendingCoaches + pendingAcademyEnquiries + pendingMCs.length + pendingOutstanding + pendingAdhoc;
  
  const pendingJoins = joinRequests.filter(j => j.status === "pending").length;
  const pendingMerchants = merchantApplications.filter(m => m.status === "pending").length;
  const pendingMemberEnquiries = membershipEnquiries.filter(e => e.status === "pending").length;
  const sectionCount = pendingMembers.length + pendingJoins + pendingMerchants + pendingMemberEnquiries + nonFootballPayments.length;
  
  const pendingOrders = orders.filter(o => o.order_status === "pending").length;

  // Helper to render empty state
  const EmptyState = ({ message }) => (
    <div className="text-center py-8 text-slate-400">
      <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-400" />
      <p>{message}</p>
    </div>
  );

  // Helper to render action buttons
  const ActionButtons = ({ item, type, approveLabel = "Approve", rejectLabel = "Reject" }) => (
    <div className="flex justify-end gap-2">
      <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white"
        onClick={() => openConfirmDialog(item, "approve", type)}
        disabled={actionLoading === item.id}>
        <CheckCircle className="w-4 h-4 mr-1" /> {approveLabel}
      </Button>
      <Button size="sm" variant="outline" className="border-red-500 text-red-400"
        onClick={() => openConfirmDialog(item, "reject", type)}
        disabled={actionLoading === item.id}>
        <XCircle className="w-4 h-4 mr-1" /> {rejectLabel}
      </Button>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading approvals...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <header className="bg-slate-950 border-b border-white/5">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/admin?tab=tools">
              <Button variant="ghost" className="text-slate-400 hover:text-white hover:bg-white/5">
                <ArrowLeft className="w-5 h-5 mr-2" /> Back to Admin Tools
              </Button>
            </Link>
            <h1 className="text-2xl font-bold text-yellow-500 flex items-center gap-2">
              <Shield className="w-7 h-7" /> Admin Approvals
            </h1>
          </div>
          <Button onClick={fetchAllData} variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-5 bg-slate-900/50 border border-yellow-500/30">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-400">Total Pending</p>
                <p className="text-3xl font-bold text-white">{academyCount + sectionCount + pendingOrders}</p>
              </div>
              <Clock className="w-10 h-10 text-yellow-500" />
            </div>
          </Card>
          <Card className="p-5 bg-slate-900/50 border border-blue-500/30">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-400">Academy</p>
                <p className="text-3xl font-bold text-white">{academyCount}</p>
              </div>
              <GraduationCap className="w-10 h-10 text-blue-400" />
            </div>
          </Card>
          <Card className="p-5 bg-slate-900/50 border border-emerald-500/30">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-emerald-400">Section</p>
                <p className="text-3xl font-bold text-white">{sectionCount}</p>
              </div>
              <Users className="w-10 h-10 text-emerald-400" />
            </div>
          </Card>
          <Card className="p-5 bg-slate-900/50 border border-purple-500/30">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-400">Orders</p>
                <p className="text-3xl font-bold text-white">{pendingOrders}</p>
              </div>
              <Package className="w-10 h-10 text-purple-400" />
            </div>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 bg-slate-900/50 shadow-md">
            <TabsTrigger value="academy" className="relative data-[state=active]:bg-blue-500/100 data-[state=active]:text-white">
              <GraduationCap className="w-4 h-4 mr-2" /> Academy
              {academyCount > 0 && <span className="ml-2 px-2 py-0.5 text-xs bg-red-500/100 text-white rounded-full">{academyCount}</span>}
            </TabsTrigger>
            <TabsTrigger value="section" className="relative data-[state=active]:bg-emerald-500/100 data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" /> Section
              {sectionCount > 0 && <span className="ml-2 px-2 py-0.5 text-xs bg-red-500/100 text-white rounded-full">{sectionCount}</span>}
            </TabsTrigger>
            <TabsTrigger value="orders" className="relative data-[state=active]:bg-purple-500/100 data-[state=active]:text-white">
              <Package className="w-4 h-4 mr-2" /> Orders
              {pendingOrders > 0 && <span className="ml-2 px-2 py-0.5 text-xs bg-red-500/100 text-white rounded-full">{pendingOrders}</span>}
            </TabsTrigger>
          </TabsList>

          {/* ACADEMY TAB */}
          <TabsContent value="academy">
            <Card className="p-6 bg-slate-900/50 shadow-lg">
              <Accordion type="single" collapsible defaultValue={activeSection || "registrations"} onValueChange={setActiveSection}>
                
                {/* Parent Registrations */}
                <AccordionItem value="registrations">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <Baby className="w-5 h-5 text-blue-400" />
                      <span className="font-semibold">Parent & Child Registrations</span>
                      {pendingRegistrations.length > 0 && <Badge className="bg-blue-500/100 text-white">{pendingRegistrations.length}</Badge>}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    {pendingRegistrations.length === 0 ? <EmptyState message="No pending registrations" /> : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Parent</TableHead>
                            <TableHead>Children</TableHead>
                            <TableHead>Contact</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pendingRegistrations.map((reg) => {
                            // Handle nested parent object
                            const parentName = reg.parent?.name || reg.parent?.given_name 
                              ? `${reg.parent?.given_name || ''} ${reg.parent?.last_name || ''}`.trim()
                              : reg.parent_name || 'N/A';
                            const parentEmail = reg.parent?.email || reg.parent_email || 'N/A';
                            const parentPhone = reg.parent?.phone || reg.phone || '';
                            // Get children names - check multiple field name variations
                            const childrenNames = reg.children?.map(c => {
                              // Try different field combinations
                              if (c.child_name) return c.child_name;
                              if (c.name) return c.name;
                              if (c.given_name || c.last_name) return `${c.given_name || ''} ${c.last_name || ''}`.trim();
                              if (c.first_name || c.family_name) return `${c.first_name || ''} ${c.family_name || ''}`.trim();
                              return 'Unnamed';
                            }).filter(Boolean).join(', ') || 'N/A';
                            
                            return (
                              <TableRow key={reg.id}>
                                <TableCell>
                                  <p className="font-medium">{parentName}</p>
                                  <p className="text-sm text-slate-400">{parentEmail}</p>
                                </TableCell>
                                <TableCell>
                                  <p className="font-medium">{reg.children?.length || 0} child(ren)</p>
                                  <p className="text-sm font-bold text-white">{childrenNames}</p>
                                </TableCell>
                                <TableCell>
                                  {parentPhone && <p className="text-sm">{parentPhone}</p>}
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {/* Academy Parent Badge - all registrations via academy are academy parents */}
                                    <Badge className="bg-blue-500/20 text-blue-400 text-xs">Academy Parent</Badge>
                                    
                                    {/* Membership Status */}
                                    {reg.is_scc_member ? (
                                      <>
                                        <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">SCC Member</Badge>
                                        <MembershipValidation 
                                          membershipNumber={reg.membership_number}
                                          showDetails={false}
                                        />
                                      </>
                                    ) : (
                                      <Badge className="bg-slate-500/20 text-slate-400 text-xs">Non-Member</Badge>
                                    )}
                                    
                                    {/* Digital Membership Status */}
                                    {reg.include_digital_membership && (
                                      <Badge className="bg-purple-500/20 text-purple-400 text-xs">
                                        +DM ${reg.digital_membership_price || reg.fee_breakdown?.dm_price || 0}
                                      </Badge>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>{formatDate(reg.created_at)}</TableCell>
                                <TableCell>{getStatusBadge(reg.status)}</TableCell>
                                <TableCell className="text-right">
                                  <div className="flex justify-end gap-2">
                                    <Button size="sm" variant="outline" onClick={() => setSelectedItem({...reg, _type: 'registration', parentName, parentEmail, childrenNames})}>
                                      <Eye className="w-4 h-4" />
                                    </Button>
                                    <ActionButtons item={reg} type="registration" />
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    )}
                  </AccordionContent>
                </AccordionItem>

                {/* Payment Verifications */}
                <AccordionItem value="payments">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <DollarSign className="w-5 h-5 text-purple-400" />
                      <span className="font-semibold">Payment Verifications</span>
                      {pendingPayments.length > 0 && <Badge className="bg-purple-500/100 text-white">{pendingPayments.length}</Badge>}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    {pendingPayments.length === 0 ? <EmptyState message="No pending payments" /> : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Reference</TableHead>
                            <TableHead>Customer</TableHead>
                            <TableHead>Amount</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pendingPayments.map((payment) => (
                            <TableRow key={payment.id}>
                              <TableCell className="font-mono font-bold text-blue-400">{payment.payment_reference}</TableCell>
                              <TableCell>
                                <p className="font-medium">{payment.parent_name || "N/A"}</p>
                                <p className="text-sm text-slate-400">{payment.parent_email}</p>
                                {payment.bank_account_holder_name && <p className="text-xs text-emerald-400">🏦 {payment.bank_account_holder_name}</p>}
                              </TableCell>
                              <TableCell className="font-semibold text-emerald-400">{formatCurrency(payment.amount)}</TableCell>
                              <TableCell>{formatDate(payment.created_at)}</TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-2">
                                  <Button size="sm" variant="outline" onClick={() => setSelectedItem(payment)}><Eye className="w-4 h-4" /></Button>
                                  <ActionButtons item={payment} type="payment" />
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                    {allPayments.length > 0 && (
                      <div className="mt-4 pt-4 border-t">
                        <Link to="/academy/payment-verification"><Button variant="outline" size="sm">View All {allPayments.length} Transactions →</Button></Link>
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>

                {/* Coach Applications */}
                <AccordionItem value="coaches">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <Trophy className="w-5 h-5 text-orange-400" />
                      <span className="font-semibold">Coach Applications</span>
                      {pendingCoaches > 0 && <Badge className="bg-orange-500/100 text-white">{pendingCoaches}</Badge>}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    {pendingCoaches === 0 ? <EmptyState message="No pending coach applications" /> : (
                      <div className="space-y-4">
                        {coachApplications.filter(c => c.status === "pending").map((coach) => (
                          <Card key={coach.id} className="p-4 border-l-4 border-l-orange-500">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-4 mb-2">
                                  <h4 className="font-semibold text-lg">{coach.full_name || `${coach.given_name} ${coach.last_name}`}</h4>
                                  {coach.is_section_member && (
                                    <Badge className="bg-emerald-500/20 text-emerald-400">Section Member</Badge>
                                  )}
                                  {coach.nroc_registered && (
                                    <Badge className="bg-blue-500/20 text-blue-400">NROC Registered</Badge>
                                  )}
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-slate-400 mb-3">
                                  <p className="flex items-center gap-2"><Mail className="w-4 h-4" /> {coach.email}</p>
                                  <p className="flex items-center gap-2"><Phone className="w-4 h-4" /> {coach.phone}</p>
                                  {coach.birthdate && <p>🎂 Born: {new Date(coach.birthdate).toLocaleDateString()}</p>}
                                  {coach.membership_number && <p>🏅 Membership #: {coach.membership_number}</p>}
                                </div>
                                
                                {/* Certifications */}
                                {coach.coaching_certifications && coach.coaching_certifications.length > 0 && (
                                  <div className="mb-2">
                                    <p className="text-xs font-semibold text-slate-400 mb-1">Coaching Certifications:</p>
                                    <div className="flex flex-wrap gap-1">
                                      {coach.coaching_certifications.map((cert, idx) => (
                                        <Badge key={idx} variant="outline" className="text-xs">{cert}</Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Currently Coaching */}
                                {coach.currently_coaching && coach.currently_coaching.length > 0 && (
                                  <div className="mb-2">
                                    <p className="text-xs font-semibold text-slate-400 mb-1">Currently Coaching:</p>
                                    <div className="flex flex-wrap gap-1">
                                      {coach.currently_coaching.map((team, idx) => (
                                        <Badge key={idx} variant="secondary" className="text-xs">{team}</Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                <p className="text-xs text-slate-400 mt-2">Applied: {formatDate(coach.created_at)}</p>
                              </div>
                              <div className="ml-4">
                                <ActionButtons item={coach} type="coach" />
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>

                {/* Academy Enquiries */}
                <AccordionItem value="academy-enquiries">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-cyan-600" />
                      <span className="font-semibold">Academy Enquiries</span>
                      {pendingAcademyEnquiries > 0 && <Badge className="bg-cyan-500 text-white">{pendingAcademyEnquiries}</Badge>}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    {pendingAcademyEnquiries === 0 ? <EmptyState message="No pending academy enquiries" /> : (
                      <div className="space-y-4">
                        {academyEnquiries.filter(e => e.status === "pending").map((enq) => (
                          <Card key={enq.id} className="p-4 border-l-4 border-l-cyan-500">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-4 mb-2">
                                  <h4 className="font-semibold text-lg">{enq.name}</h4>
                                  <span className="text-sm text-slate-400">{formatDate(enq.created_at)}</span>
                                </div>
                                <div className="flex gap-4 text-sm text-slate-400 mb-3">
                                  <span>📧 {enq.email}</span>
                                  {enq.phone && <span>📱 {enq.phone}</span>}
                                </div>
                                <div className="bg-[#0a0f1a] p-3 rounded-lg">
                                  <p className="text-sm font-medium text-slate-300 mb-1">Message:</p>
                                  <p className="text-white whitespace-pre-wrap">{enq.message}</p>
                                </div>
                              </div>
                              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white ml-4"
                                onClick={() => handleEnquiryContacted(enq.id, "academy")} disabled={actionLoading === enq.id}>
                                <Phone className="w-4 h-4 mr-1" /> Mark Contacted
                              </Button>
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>

                {/* MC Approvals */}
                <AccordionItem value="mc-approvals">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <Stethoscope className="w-5 h-5 text-red-400" />
                      <span className="font-semibold">Medical Certificate Approvals</span>
                      {pendingMCs.length > 0 && <Badge className="bg-red-500/100 text-white">{pendingMCs.length}</Badge>}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    {pendingMCs.length === 0 ? <EmptyState message="No pending MC approvals" /> : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Player</TableHead>
                            <TableHead>Session Date</TableHead>
                            <TableHead>MC Document</TableHead>
                            <TableHead>Submitted</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pendingMCs.map((mc) => (
                            <TableRow key={mc.id}>
                              <TableCell className="font-medium">{mc.player_name || "Unknown"}</TableCell>
                              <TableCell>{formatDate(mc.session_date)}</TableCell>
                              <TableCell>
                                {mc.mc_file_url && (
                                  <a href={mc.mc_file_url} target="_blank" rel="noreferrer" className="text-blue-400 hover:underline flex items-center gap-1">
                                    <FileText className="w-4 h-4" /> View MC
                                  </a>
                                )}
                              </TableCell>
                              <TableCell>{formatDate(mc.mc_submitted_at)}</TableCell>
                              <TableCell className="text-right"><ActionButtons item={mc} type="mc" /></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </AccordionContent>
                </AccordionItem>

                {/* Outstanding Payments */}
                <AccordionItem value="outstanding">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-red-400" />
                      <span className="font-semibold">Outstanding Payments</span>
                      {pendingOutstanding > 0 && <Badge className="bg-red-500/100 text-white">{pendingOutstanding}</Badge>}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    {outstandingPayments.length === 0 ? <EmptyState message="No outstanding payments" /> : (
                      <div className="space-y-4">
                        {outstandingPayments.map((payment) => (
                          <Card key={payment.id} className={`p-4 ${payment.status === "paid" ? "bg-emerald-500/10" : payment.status === "waived" ? "bg-[#0a0f1a]" : "bg-red-500/10"}`}>
                            <div className="flex justify-between items-start">
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{payment.parent_name}</span>
                                  {getStatusBadge(payment.status)}
                                  {payment.source === "registration_invoice" && (
                                    <Badge className="bg-indigo-500/20 text-indigo-300" data-testid="registration-invoice-badge">Registration invoice</Badge>
                                  )}
                                </div>
                                <div className="text-sm text-slate-400">
                                  <p><Mail className="w-3 h-3 inline mr-1" />{payment.parent_email}</p>
                                  <p className="font-mono text-xs mt-1">Ref: {payment.payment_reference}</p>
                                </div>
                                <div className="text-sm">
                                  <span className="text-slate-400">Amount Due: </span>
                                  <span className="font-bold text-red-400">${Number((payment.source === "registration_invoice" ? payment.amount_due : payment.difference_amount) || 0).toFixed(2)}</span>
                                </div>
                                <div className="text-xs text-slate-400">
                                  {payment.source !== "registration_invoice" && (
                                    <p>Original paid: ${payment.original_amount_paid?.toFixed(2)} → Should be: ${payment.correct_amount?.toFixed(2)}</p>
                                  )}
                                  <p>Created: {formatDate(payment.created_at)}</p>
                                  {payment.email_sent_at && (
                                    <p className="text-emerald-400">Email sent: {formatDate(payment.email_sent_at)} ({payment.email_sent_count}x)</p>
                                  )}
                                  {payment.term_name && <p>Term: {payment.term_name}</p>}
                                </div>
                              </div>
                              <div className="flex flex-col gap-2">
                                {payment.source === "registration_invoice" ? (
                                  <Link to={`/admin/payments?ref=${encodeURIComponent(payment.payment_reference || "")}`}>
                                    <Button size="sm" variant="outline" data-testid="open-in-payments-hub-btn">
                                      <ExternalLink className="w-4 h-4 mr-1" /> Manage in Payments Hub
                                    </Button>
                                  </Link>
                                ) : payment.status !== "paid" && payment.status !== "waived" && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={async () => {
                                        try {
                                          setActionLoading(payment.id);
                                          await axios.post(`${API}/academy/admin/outstanding-payments/${payment.id}/resend-email`, {}, { withCredentials: true });
                                          toast.success("Email resent!");
                                          fetchAllData();
                                        } catch (err) {
                                          toast.error(err.response?.data?.detail || "Failed to resend email");
                                        } finally {
                                          setActionLoading(null);
                                        }
                                      }}
                                      disabled={actionLoading === payment.id}
                                    >
                                      <Mail className="w-4 h-4 mr-1" /> Resend Email
                                    </Button>
                                    <Button
                                      size="sm"
                                      className="bg-green-600 hover:bg-green-700"
                                      onClick={async () => {
                                        if (!confirm("Mark this payment as paid?")) return;
                                        try {
                                          setActionLoading(payment.id);
                                          await axios.post(`${API}/academy/admin/outstanding-payments/${payment.id}/mark-paid`, { note: "Manually marked paid" }, { withCredentials: true });
                                          toast.success("Payment marked as paid");
                                          fetchAllData();
                                        } catch (err) {
                                          toast.error(err.response?.data?.detail || "Failed");
                                        } finally {
                                          setActionLoading(null);
                                        }
                                      }}
                                      disabled={actionLoading === payment.id}
                                    >
                                      <CheckCircle className="w-4 h-4 mr-1" /> Mark Paid
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-orange-400 border-orange-500/30"
                                      onClick={async () => {
                                        const reason = prompt("Reason for waiving payment:");
                                        if (!reason) return;
                                        try {
                                          setActionLoading(payment.id);
                                          await axios.post(`${API}/academy/admin/outstanding-payments/${payment.id}/waive`, { note: reason }, { withCredentials: true });
                                          toast.success("Payment waived");
                                          fetchAllData();
                                        } catch (err) {
                                          toast.error(err.response?.data?.detail || "Failed");
                                        } finally {
                                          setActionLoading(null);
                                        }
                                      }}
                                      disabled={actionLoading === payment.id}
                                    >
                                      <XCircle className="w-4 h-4 mr-1" /> Waive
                                    </Button>
                                  </>
                                )}
                                {payment.status === "paid" && (
                                  <Badge className="bg-emerald-500/20 text-emerald-400 text-center">
                                    ✓ Paid {payment.paid_at ? formatDate(payment.paid_at) : ""}
                                  </Badge>
                                )}
                                {payment.status === "waived" && (
                                  <Badge className="bg-slate-800/50 text-white text-center">
                                    Waived
                                  </Badge>
                                )}
                              </div>
                            </div>
                            {payment.notes && payment.notes.length > 0 && (
                              <div className="mt-3 pt-3 border-t">
                                <p className="text-xs font-medium text-slate-400 mb-1">Notes:</p>
                                {payment.notes.map((note, idx) => (
                                  <p key={idx} className="text-xs text-slate-400">• {note.text} <span className="text-slate-400">({formatDate(note.at)})</span></p>
                                ))}
                              </div>
                            )}
                          </Card>
                        ))}
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>

                {/* Ad-hoc Session Bookings */}
                <AccordionItem value="adhoc-bookings">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-teal-600" />
                      <span className="font-semibold">Ad-hoc Session Bookings</span>
                      {pendingAdhoc > 0 && <Badge className="bg-teal-500/100 text-white">{pendingAdhoc}</Badge>}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    {adhocBookings.length === 0 ? <EmptyState message="No ad-hoc bookings" /> : (
                      <div className="space-y-4">
                        {adhocBookings.map((booking) => (
                          <Card key={booking.id} className={`p-4 ${booking.payment_status === "paid" ? "bg-emerald-500/10" : "bg-teal-500/10"}`}>
                            <div className="flex justify-between items-start">
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{booking.player_name}</span>
                                  {getStatusBadge(booking.payment_status)}
                                  <Badge variant="outline" className={booking.booking_type === "term_upgrade" ? "bg-blue-500/20 text-blue-400" : "bg-purple-500/20 text-purple-400"}>
                                    {booking.booking_type === "term_upgrade" ? "Term Upgrade" : "Single Session"}
                                  </Badge>
                                </div>
                                <div className="text-sm text-slate-400">
                                  <p><strong>Session:</strong> {booking.session_name}</p>
                                  <p className="font-mono text-xs mt-1">Ref: {booking.booking_reference}</p>
                                  {booking.parent_name && <p><Mail className="w-3 h-3 inline mr-1" />{booking.parent_name} ({booking.parent_email})</p>}
                                </div>
                                <div className="text-sm">
                                  <span className="text-slate-400">Amount: </span>
                                  <span className="font-bold text-teal-600">${booking.total_amount?.toFixed(2)}</span>
                                </div>
                                <div className="text-xs text-slate-400">
                                  <p>Created: {formatDate(booking.created_at)}</p>
                                  {booking.paid_at && <p className="text-emerald-400">Paid: {formatDate(booking.paid_at)}</p>}
                                </div>
                              </div>
                              <div className="flex flex-col gap-2">
                                {booking.payment_status === "pending" && (
                                  <Button
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700"
                                    onClick={async () => {
                                      if (!confirm("Confirm payment received for this booking?")) return;
                                      try {
                                        setActionLoading(booking.id);
                                        await axios.post(`${API}/academy/adhoc-bookings/${booking.id}/confirm-payment`, {}, { withCredentials: true });
                                        toast.success("Payment confirmed!");
                                        fetchAllData();
                                      } catch (err) {
                                        toast.error(err.response?.data?.detail || "Failed to confirm payment");
                                      } finally {
                                        setActionLoading(null);
                                      }
                                    }}
                                    disabled={actionLoading === booking.id}
                                  >
                                    <CheckCircle className="w-4 h-4 mr-1" /> Confirm Payment
                                  </Button>
                                )}
                                {booking.payment_status === "paid" && (
                                  <Badge className="bg-emerald-500/20 text-emerald-400 text-center">
                                    ✓ Paid
                                  </Badge>
                                )}
                                {/* Cancel booking (soft-delete/archive). Single
                                    confirm for unpaid; backend gates paid behind
                                    an extra "this was paid" confirmation. */}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                                  data-testid={`adhoc-cancel-btn-${booking.id}`}
                                  disabled={actionLoading === booking.id}
                                  onClick={async () => {
                                    const reason = window.prompt("Reason for cancelling this booking (logged to audit trail, min 5 chars):", "");
                                    if (reason === null) return;
                                    if (reason.trim().length < 5) { toast.error("A reason of at least 5 characters is required"); return; }
                                    if (!window.confirm(`Cancel booking ${booking.booking_reference}? It will be archived (recoverable), not deleted.`)) return;
                                    const doCancel = (confirmPaid) =>
                                      axios.post(`${API}/academy/admin/adhoc-bookings/${booking.id}/cancel`,
                                        { reason: reason.trim(), confirm_paid: confirmPaid }, { withCredentials: true });
                                    try {
                                      setActionLoading(booking.id);
                                      await doCancel(false);
                                      toast.success("Booking cancelled (archived)");
                                      fetchAllData();
                                    } catch (err) {
                                      const d = err.response?.data?.detail;
                                      const code = typeof d === "object" ? d?.code : null;
                                      if (err.response?.status === 409 && code === "requires_paid_confirmation") {
                                        if (window.confirm("⚠️ This booking was PAID / confirmed — are you sure you want to cancel it?\n\nIt will be archived (recoverable), not deleted.")) {
                                          try {
                                            await doCancel(true);
                                            toast.success("Paid booking cancelled (archived)");
                                            fetchAllData();
                                          } catch (e2) {
                                            toast.error(e2.response?.data?.detail?.message || e2.response?.data?.detail || "Cancel failed");
                                          }
                                        }
                                      } else {
                                        toast.error((typeof d === "string" ? d : d?.message) || "Cancel failed");
                                      }
                                    } finally {
                                      setActionLoading(null);
                                    }
                                  }}
                                >
                                  <XCircle className="w-4 h-4 mr-1" /> Cancel booking
                                </Button>
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>

                {/* Unassigned Players */}
                <AccordionItem value="unassigned-players">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-purple-400" />
                      <span className="font-semibold">Unassigned Players</span>
                      {unassignedPlayers.length > 0 && (
                        <Badge className="bg-purple-500/100">{unassignedPlayers.length}</Badge>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    {unassignedPlayers.length === 0 ? <EmptyState message="All players are assigned to teams" /> : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Player</TableHead>
                            <TableHead>Age</TableHead>
                            <TableHead>Gender</TableHead>
                            <TableHead>ID Doc</TableHead>
                            <TableHead>Parent</TableHead>
                            <TableHead>Contact</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {unassignedPlayers.map((player) => {
                            // Calculate age from birthdate
                            const calculateAge = (birthdate) => {
                              if (!birthdate) return "N/A";
                              const today = new Date();
                              const birth = new Date(birthdate);
                              let age = today.getFullYear() - birth.getFullYear();
                              const m = today.getMonth() - birth.getMonth();
                              if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
                              return age;
                            };
                            return (
                            <TableRow key={player.id}>
                              <TableCell>
                                <div>
                                  <p className="font-medium">{player.child_name || `${player.given_name} ${player.last_name}`}</p>
                                  <p className="text-xs text-slate-400">{player.membership_number}</p>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className="font-bold">{calculateAge(player.birthdate)} yrs</Badge>
                              </TableCell>
                              <TableCell>
                                <Badge className={player.gender === 'female' ? 'bg-pink-500/100' : 'bg-blue-500/100'}>
                                  {player.gender === 'female' ? '♀ F' : '♂ M'}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-xs">{player.id_document_number || "N/A"}</TableCell>
                              <TableCell>
                                <div>
                                  <p className="font-medium">{player.parent_name || "N/A"}</p>
                                  <p className="text-xs text-slate-400">{player.parent_email}</p>
                                </div>
                              </TableCell>
                              <TableCell>{player.parent_phone || player.emergency_contact || "N/A"}</TableCell>
                              <TableCell>
                                <select
                                  className="text-sm border rounded px-2 py-1"
                                  defaultValue=""
                                  onChange={async (e) => {
                                    if (e.target.value) {
                                      try {
                                        await axios.post(
                                          `${API}/academy/admin/assign-player-to-team?player_id=${player.id}&team_id=${e.target.value}`,
                                          {},
                                          { withCredentials: true }
                                        );
                                        toast.success("Player assigned to team!");
                                        fetchAllData();
                                      } catch (error) {
                                        toast.error("Failed to assign player");
                                      }
                                    }
                                  }}
                                >
                                  <option value="">Assign to team...</option>
                                  {academyTeams.map((team) => (
                                    <option key={team.id} value={team.id}>{team.name}</option>
                                  ))}
                                </select>
                              </TableCell>
                            </TableRow>
                          )})}
                        </TableBody>
                      </Table>
                    )}
                  </AccordionContent>
                </AccordionItem>

              </Accordion>
            </Card>
          </TabsContent>

          {/* SECTION TAB */}
          <TabsContent value="section">
            <Card className="p-6 bg-slate-900/50 shadow-lg">
              <Accordion type="single" collapsible defaultValue={activeSection || "members"} onValueChange={setActiveSection}>
                
                {/* Member Approvals */}
                <AccordionItem value="members">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <UserPlus className="w-5 h-5 text-emerald-400" />
                      <span className="font-semibold">New Member Approvals</span>
                      {pendingMembers.length > 0 && <Badge className="bg-emerald-500/100 text-white">{pendingMembers.length}</Badge>}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    {pendingMembers.length === 0 ? <EmptyState message="No pending member approvals" /> : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Email / Phone</TableHead>
                            <TableHead>Membership Type</TableHead>
                            <TableHead>Registration</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pendingMembers.map((member) => (
                            <TableRow key={member.id}>
                              <TableCell className="font-medium">{member.name}</TableCell>
                              <TableCell>
                                <div>
                                  <p className="text-sm">{member.email}</p>
                                  <p className="text-xs text-slate-400">{member.phone || "No phone"}</p>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="space-y-1">
                                  {member.is_digital_member && (
                                    <Badge className="bg-purple-500/20 text-purple-400">Digital Member</Badge>
                                  )}
                                  {member.is_scc_member && (
                                    <Badge className="bg-emerald-500/20 text-emerald-400">SCC Member</Badge>
                                  )}
                                  {member.member_type === 'section' && (
                                    <Badge className="bg-blue-500/20 text-blue-400">Section Member</Badge>
                                  )}
                                  {!member.is_digital_member && !member.is_scc_member && member.member_type !== 'section' && (
                                    <Badge variant="outline">Non-Member</Badge>
                                  )}
                                  {member.membership_validation_flagged && (
                                    <FlagResolutionBadge
                                      member={member}
                                      onResolved={() => fetchAllData()}
                                    />
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="space-y-1">
                                  {member.roles?.includes('academy_parent') && (
                                    <Badge className="bg-orange-500/20 text-orange-400">Academy Parent</Badge>
                                  )}
                                  {member.academy_parent_children?.length > 0 && (
                                    <p className="text-xs text-slate-400">{member.academy_parent_children.length} child(ren)</p>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>{formatDate(member.created_at)}</TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setSelectedMember(member)}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  <ActionButtons item={member} type="member" approveLabel="Authorize" />
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </AccordionContent>
                </AccordionItem>

                {/* Team Join Requests */}
                <AccordionItem value="teams">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-blue-400" />
                      <span className="font-semibold">Team Join Requests</span>
                      {pendingJoins > 0 && <Badge className="bg-blue-500/100 text-white">{pendingJoins}</Badge>}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    {pendingJoins === 0 ? <EmptyState message="No pending team join requests" /> : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>User</TableHead>
                            <TableHead>Team</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {joinRequests.filter(j => j.status === "pending").map((req) => (
                            <TableRow key={req.id}>
                              <TableCell><p className="font-medium">{req.user_name}</p><p className="text-sm text-slate-400">{req.user_email}</p></TableCell>
                              <TableCell>{req.team_name}</TableCell>
                              <TableCell>{formatDate(req.created_at)}</TableCell>
                              <TableCell className="text-right"><ActionButtons item={req} type="joinRequest" /></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </AccordionContent>
                </AccordionItem>

                {/* Merchant Applications */}
                <AccordionItem value="merchants">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <Store className="w-5 h-5 text-purple-400" />
                      <span className="font-semibold">Merchant Applications</span>
                      {pendingMerchants > 0 && <Badge className="bg-purple-500/100 text-white">{pendingMerchants}</Badge>}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    {pendingMerchants === 0 ? <EmptyState message="No pending merchant applications" /> : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Business</TableHead>
                            <TableHead>Contact</TableHead>
                            <TableHead>Category</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {merchantApplications.filter(m => m.status === "pending").map((app) => (
                            <TableRow key={app.id}>
                              <TableCell><p className="font-medium">{app.business_name}</p><p className="text-sm text-slate-400 truncate max-w-[200px]">{app.description}</p></TableCell>
                              <TableCell><p>{app.contact_email}</p><p className="text-sm text-slate-400">{app.contact_phone}</p></TableCell>
                              <TableCell>{app.category}</TableCell>
                              <TableCell>{formatDate(app.created_at)}</TableCell>
                              <TableCell className="text-right"><ActionButtons item={app} type="merchant" /></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </AccordionContent>
                </AccordionItem>

                {/* Membership Enquiries */}
                <AccordionItem value="membership-enquiries">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-teal-600" />
                      <span className="font-semibold">Membership Enquiries</span>
                      {pendingMemberEnquiries > 0 && <Badge className="bg-teal-500/100 text-white">{pendingMemberEnquiries}</Badge>}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    {pendingMemberEnquiries === 0 ? <EmptyState message="No pending membership enquiries" /> : (
                      <div className="space-y-4">
                        {membershipEnquiries.filter(e => e.status === "pending").map((enq) => (
                          <Card key={enq.id} className="p-4 border-l-4 border-l-teal-500">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-4 mb-2">
                                  <h4 className="font-semibold text-lg">{enq.name}</h4>
                                  <span className="text-sm text-slate-400">{formatDate(enq.created_at)}</span>
                                </div>
                                <div className="flex gap-4 text-sm text-slate-400 mb-3">
                                  <span>📧 {enq.email}</span>
                                  {enq.phone && <span>📱 {enq.phone}</span>}
                                  {enq.referral_code && <span>🎫 Referral: {enq.referral_code}</span>}
                                </div>
                                <div className="bg-[#0a0f1a] p-3 rounded-lg">
                                  <p className="text-sm font-medium text-slate-300 mb-1">Message:</p>
                                  <p className="text-white whitespace-pre-wrap">{enq.message}</p>
                                </div>
                              </div>
                              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white ml-4"
                                onClick={() => handleEnquiryContacted(enq.id, "membership")} disabled={actionLoading === enq.id}>
                                <Phone className="w-4 h-4 mr-1" /> Mark Contacted
                              </Button>
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>

                {/* Non-Football Payment Proofs */}
                <AccordionItem value="non-football">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <CreditCard className="w-5 h-5 text-indigo-400" />
                      <span className="font-semibold">Non-Football Payment Proofs</span>
                      {nonFootballPayments.length > 0 && <Badge className="bg-indigo-500/100 text-white">{nonFootballPayments.length}</Badge>}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    {nonFootballPayments.length === 0 ? <EmptyState message="No pending payment proofs" /> : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Payment Proof</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {nonFootballPayments.map((user) => (
                            <TableRow key={user.id}>
                              <TableCell className="font-medium">{user.name}</TableCell>
                              <TableCell>{user.email}</TableCell>
                              <TableCell>
                                {user.payment_proof_url && (
                                  <a href={user.payment_proof_url} target="_blank" rel="noreferrer" className="text-blue-400 hover:underline flex items-center gap-1">
                                    <FileText className="w-4 h-4" /> View Proof
                                  </a>
                                )}
                              </TableCell>
                              <TableCell>{formatDate(user.created_at)}</TableCell>
                              <TableCell className="text-right"><ActionButtons item={user} type="member" approveLabel="Authorize" /></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </AccordionContent>
                </AccordionItem>

              </Accordion>
            </Card>
          </TabsContent>

          {/* ORDERS TAB */}
          <TabsContent value="orders">
            <Card className="p-6 bg-slate-900/50 shadow-lg">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><Package className="w-5 h-5" /> Order Management</h3>
              {orders.length === 0 ? <EmptyState message="No orders found" /> : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order ID</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Payment</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell className="font-mono text-sm">{order.id?.substring(0, 8)}...</TableCell>
                        <TableCell><p className="font-medium">{order.user_name}</p><p className="text-sm text-slate-400">{order.user_email}</p></TableCell>
                        <TableCell>{order.items?.length || 0} items</TableCell>
                        <TableCell className="font-semibold text-emerald-400">{formatCurrency(order.total_amount)}</TableCell>
                        <TableCell>{getStatusBadge(order.payment_status)}</TableCell>
                        <TableCell>{getStatusBadge(order.order_status)}</TableCell>
                        <TableCell>{formatDate(order.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <select 
                            className="border rounded px-2 py-1 text-sm"
                            value={order.order_status}
                            onChange={(e) => handleOrderStatus(order.id, e.target.value)}
                            disabled={actionLoading === order.id}
                          >
                            <option value="pending">Pending</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="processing">Processing</option>
                            <option value="shipped">Shipped</option>
                            <option value="delivered">Delivered</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Details Dialog - handles payments and registrations */}
      <Dialog open={!!selectedItem} onOpenChange={() => { setSelectedItem(null); setMemberOverride({ editing: false, memberType: '', saving: false }); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedItem?._type === 'registration' ? 'Registration Details' : 'Payment Details'}
            </DialogTitle>
            <DialogDescription>
              {selectedItem?._type === 'registration' 
                ? `Registration ID: ${selectedItem?.id?.substring(0, 8)}...`
                : `Reference: ${selectedItem?.payment_reference}`}
            </DialogDescription>
          </DialogHeader>
          {selectedItem && selectedItem._type === 'registration' ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-400">Parent Name</p>
                  <p className="font-medium">{selectedItem.parentName}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Email</p>
                  <p className="font-medium">{selectedItem.parentEmail}</p>
                </div>
              </div>
              {selectedItem.parent?.phone && (
                <div>
                  <p className="text-sm text-slate-400">Phone</p>
                  <p className="font-medium">{selectedItem.parent.phone}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-slate-400">Residential Address</p>
                <p className="font-medium">{selectedItem.residential_address || 'N/A'}</p>
              </div>
              <div className="flex gap-2">
                {selectedItem.is_scc_member && <Badge className="bg-emerald-500/20 text-emerald-400">SCC Member</Badge>}
              </div>
              
              {/* Member Status Display */}
              {selectedItem.is_scc_member ? (
                <div className="p-4 rounded-lg border-2 bg-emerald-500/10 border-emerald-500/30">
                  <p className="text-sm text-slate-400 mb-2">Membership Verification</p>
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">🏅</span>
                    <div className="flex-1">
                      <p className="text-lg font-bold text-emerald-400">Claims SCC Member</p>
                      
                      {/* Membership Number Display */}
                      {selectedItem.membership_number ? (
                        <div className="mt-2 p-3 bg-slate-900/50 rounded-lg">
                          <p className="text-sm text-slate-400 mb-1">Membership Number:</p>
                          <p className="text-xl font-mono font-bold text-white mb-2">{selectedItem.membership_number}</p>
                          
                          {/* Simple Validation */}
                          {(() => {
                            const validation = validateMembershipNumber(selectedItem.membership_number);
                            return validation.valid ? (
                              <p className="text-sm text-slate-400">
                                Please verify this is a valid Singapore Cricket Club membership number.
                              </p>
                            ) : (
                              <div className="p-2 rounded bg-amber-500/20 border border-amber-500/30">
                                <p className="text-amber-400 text-sm font-medium">⚠️ Please verify membership</p>
                                <p className="text-amber-300 text-xs mt-1">
                                  This number may not match SCC membership format. Non-member pricing will apply if not a valid SCC member.
                                </p>
                              </div>
                            );
                          })()}
                          
                          {/* Request Proof Button */}
                          <Button
                            onClick={() => handleRequestMembershipProof(selectedItem.id)}
                            disabled={requestingProof === selectedItem.id}
                            size="sm"
                            className="mt-3 bg-amber-600 hover:bg-amber-700 text-white"
                          >
                            {requestingProof === selectedItem.id ? (
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Mail className="w-4 h-4 mr-2" />
                            )}
                            Request Membership Card
                          </Button>
                          {selectedItem.membership_proof_requested && (
                            <p className="text-xs text-emerald-400 mt-2">
                              ✓ Proof requested on {new Date(selectedItem.membership_proof_requested_at).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="mt-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                          <p className="text-amber-400 font-medium">⚠️ No membership number provided</p>
                          <p className="text-amber-300 text-xs mt-1">
                            Parent claims SCC member but did not provide membership number. Please verify - non-member pricing will apply if not valid.
                          </p>
                          
                          {/* Request Proof Button */}
                          <Button
                            onClick={() => handleRequestMembershipProof(selectedItem.id)}
                            disabled={requestingProof === selectedItem.id}
                            size="sm"
                            className="mt-3 bg-amber-600 hover:bg-amber-700 text-white"
                          >
                            {requestingProof === selectedItem.id ? (
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Mail className="w-4 h-4 mr-2" />
                            )}
                            Request Membership Card
                          </Button>
                          {selectedItem.membership_proof_requested && (
                            <p className="text-xs text-emerald-400 mt-2">
                              ✓ Proof requested on {new Date(selectedItem.membership_proof_requested_at).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      )}
                      
                      <p className="text-xs text-slate-400 mt-2">Member discount applies only for verified SCC members</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 rounded-lg border-2 bg-slate-800/50 border-slate-600">
                  <p className="text-sm text-slate-400 mb-1">Member Status</p>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">👤</span>
                    <div>
                      <p className="text-lg font-bold text-slate-300">Non-Member</p>
                      <p className="text-xs text-slate-400 mt-1">Standard pricing applies</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Digital Membership Status */}
              {selectedItem.include_digital_membership && (
                <div className="p-4 rounded-lg border-2 bg-purple-500/10 border-purple-500/30">
                  <p className="text-sm text-slate-400 mb-1">Digital Membership</p>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">💳</span>
                    <div>
                      <p className="text-lg font-bold text-purple-400">Digital Membership Purchased</p>
                      <p className="text-xl font-bold text-white">
                        ${selectedItem.digital_membership_price || selectedItem.fee_breakdown?.dm_price || 0}
                      </p>
                      {selectedItem.is_scc_member && selectedItem.member_type !== 'section' && (
                        <p className="text-xs text-amber-400 mt-1">
                          ⚠️ Note: Club Member purchasing DM. If Club discount ≥ DM discount, no additional term fee discount applies. 
                          DM provides Section-level benefits, merch/merchant discounts.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Admin Override Member Status */}
              <div className="p-4 rounded-lg border-2 bg-amber-500/10 border-amber-500/30">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-amber-400 font-medium flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    Override Member Status
                  </p>
                  {selectedItem.member_status_override && (
                    <Badge className="bg-amber-500/20 text-amber-400 text-xs">Previously Overridden</Badge>
                  )}
                </div>
                
                {!memberOverride.editing ? (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-400">Current Status:</p>
                      <p className="text-sm font-medium text-white capitalize">
                        {selectedItem.member_type?.replace('_', ' ') || (selectedItem.is_scc_member ? 'SCC Member' : 'Non-Member')}
                      </p>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
                      onClick={() => setMemberOverride({ 
                        editing: true, 
                        memberType: selectedItem.member_type || (selectedItem.is_scc_member ? 'scc_member' : 'non_member'),
                        saving: false 
                      })}
                    >
                      Change
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Select
                      value={memberOverride.memberType}
                      onValueChange={(value) => setMemberOverride(prev => ({ ...prev, memberType: value }))}
                    >
                      <SelectTrigger className="bg-[#0a0f1a] border-slate-700 text-white">
                        <SelectValue placeholder="Select member type" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0a0f1a] border-slate-700">
                        <SelectItem value="section" className="text-white hover:bg-slate-800">
                          Section Member (Full discount)
                        </SelectItem>
                        <SelectItem value="scc_member" className="text-white hover:bg-slate-800">
                          Club Member (Partial discount)
                        </SelectItem>
                        <SelectItem value="digital" className="text-white hover:bg-slate-800">
                          Digital Member
                        </SelectItem>
                        <SelectItem value="non_member" className="text-white hover:bg-slate-800">
                          Non-Member (No discount)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 border-slate-600"
                        onClick={() => setMemberOverride({ editing: false, memberType: '', saving: false })}
                        disabled={memberOverride.saving}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1 bg-amber-600 hover:bg-amber-700"
                        onClick={() => handleMemberStatusOverride(selectedItem.id, memberOverride.memberType)}
                        disabled={memberOverride.saving}
                      >
                        {memberOverride.saving ? (
                          <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Save className="h-4 w-4 mr-2" />
                        )}
                        Save
                      </Button>
                    </div>
                    <p className="text-xs text-slate-400">
                      Note: Changing member status will affect pricing calculations for future registrations by this parent.
                    </p>
                  </div>
                )}
              </div>

              <div className="border-t pt-4">
                <p className="text-sm font-medium text-slate-300 mb-2">Children ({selectedItem.children?.length || 0})</p>
                <div className="space-y-2">
                  {selectedItem.children?.map((child, idx) => {
                    // Get child name from various possible fields
                    const childName = child.child_name || child.name || 
                      (child.given_name || child.last_name ? `${child.given_name || ''} ${child.last_name || ''}`.trim() : null) ||
                      (child.first_name || child.family_name ? `${child.first_name || ''} ${child.family_name || ''}`.trim() : null) ||
                      'Unnamed Child';
                    
                    return (
                      <div key={idx} className="bg-[#0a0f1a] p-3 rounded-lg border border-blue-500/30">
                        <p className="font-bold text-lg text-blue-400">{childName}</p>
                        <div className="text-sm text-slate-400 grid grid-cols-2 gap-2 mt-2">
                          <span>🎂 DOB: {child.birthdate || 'N/A'}</span>
                          <span>👤 Gender: {child.gender || 'N/A'}</span>
                          {child.emergency_contact && <span>📞 Emergency: {child.emergency_contact}</span>}
                          {child.emergency_relationship && <span>👨‍👩‍👧 Relationship: {child.emergency_relationship}</span>}
                        </div>
                        {child.medical_info && <p className="text-sm text-orange-400 mt-2">⚕️ Medical: {child.medical_info}</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="text-sm text-slate-400">Submitted: {formatDate(selectedItem.created_at)}</div>
            </div>
          ) : selectedItem && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-sm text-slate-400">Customer</p><p className="font-medium">{selectedItem.parent_name}</p><p className="text-sm text-slate-400">{selectedItem.parent_email}</p></div>
                <div><p className="text-sm text-slate-400">Total Amount</p><p className="text-2xl font-bold text-emerald-400">{formatCurrency(selectedItem.amount)}</p></div>
              </div>
              
              {/* Payment Type */}
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <p className="text-sm text-blue-400 font-medium">📋 Payment Type</p>
                <p className="text-lg font-bold text-blue-300 capitalize">{selectedItem.payment_type?.replace(/_/g, ' ') || 'N/A'}</p>
              </div>

              {/* Term Info */}
              {selectedItem.term_name && (
                <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                  <p className="text-sm text-purple-400 font-medium">📅 Term</p>
                  <p className="text-lg font-bold text-purple-300">{selectedItem.term_name}</p>
                  {selectedItem.fee_breakdown?.term_weeks && (
                    <p className="text-sm text-purple-400">{selectedItem.fee_breakdown.term_weeks} weeks</p>
                  )}
                </div>
              )}

              {/* Per-Child Session Breakdown */}
              {selectedItem.fee_breakdown?.child_breakdowns?.length > 0 && (
                <div className="border rounded-lg p-3">
                  <p className="text-sm font-medium text-slate-300 mb-3">👶 Children & Sessions</p>
                  <div className="space-y-4">
                    {selectedItem.fee_breakdown.child_breakdowns.map((child, idx) => (
                      <div key={idx} className="p-3 bg-slate-800/50 rounded-lg">
                        <p className="font-semibold text-white mb-2">{child.name}</p>
                        <div className="space-y-1 text-sm">
                          {child.sessions?.map((sess, sIdx) => (
                            <div key={sIdx} className="flex justify-between text-slate-400">
                              <span>{['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][sess.day]} {sess.time}</span>
                              <span>${sess.finalPrice?.toFixed(2) || sess.price?.toFixed(2)}/session</span>
                            </div>
                          ))}
                          <div className="flex justify-between pt-1 border-t border-slate-700 text-slate-300">
                            <span>{child.sessionCount} session(s)/week × {selectedItem.fee_breakdown?.term_weeks || 10} weeks</span>
                            <span className="font-medium">${child.termFee?.toFixed(2)}</span>
                          </div>
                          {child.memberDiscount > 0 && (
                            <div className="flex justify-between text-emerald-400">
                              <span>Member discount ({child.memberDiscountPercent}%)</span>
                              <span>-${child.memberDiscount?.toFixed(2)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Fee Breakdown Summary */}
              {selectedItem.fee_breakdown && (
                <div className="border rounded-lg p-3">
                  <p className="text-sm font-medium text-slate-300 mb-2">💰 Fee Summary</p>
                  <div className="space-y-2 text-sm">
                    {selectedItem.fee_breakdown.session_fees > 0 && (
                      <div className="flex justify-between">
                        <span>Session Fees ({selectedItem.fee_breakdown.children_count || 1} child(ren))</span>
                        <span className="font-medium">${selectedItem.fee_breakdown.session_fees?.toFixed(2)}</span>
                      </div>
                    )}
                    {/* Show base_fee if session_fees not available */}
                    {!selectedItem.fee_breakdown.session_fees && selectedItem.fee_breakdown.base_fee > 0 && (
                      <div className="flex justify-between">
                        <span>Session Fees ({selectedItem.fee_breakdown.children_count || 1} child(ren))</span>
                        <span className="font-medium">${selectedItem.fee_breakdown.base_fee?.toFixed(2)}</span>
                      </div>
                    )}
                    {selectedItem.fee_breakdown.member_discount > 0 && (
                      <div className="flex justify-between text-emerald-400">
                        <span>Member Discount ({selectedItem.fee_breakdown.member_discount_percent}%)</span>
                        <span>-${selectedItem.fee_breakdown.member_discount?.toFixed(2)}</span>
                      </div>
                    )}
                    {selectedItem.fee_breakdown.sibling_discount > 0 && (
                      <div className="flex justify-between text-emerald-400">
                        <span>👨‍👩‍👧‍👦 Sibling Discount ({selectedItem.fee_breakdown.sibling_discount_percent}%)</span>
                        <span>Savings of ${selectedItem.fee_breakdown.sibling_discount?.toFixed(2)} included</span>
                      </div>
                    )}
                    {selectedItem.fee_breakdown.registration_fee > 0 && (
                      <div className="flex justify-between">
                        <span>Registration Fee</span>
                        <span className="font-medium">${selectedItem.fee_breakdown.registration_fee?.toFixed(2)}</span>
                      </div>
                    )}
                    {(selectedItem.fee_breakdown.digital_membership_fee > 0 || selectedItem.fee_breakdown.dm_price > 0) && (
                      <div className="flex justify-between text-purple-400">
                        <span>Digital Membership (1 year)</span>
                        <span className="font-medium">${(selectedItem.fee_breakdown.digital_membership_fee || selectedItem.fee_breakdown.dm_price)?.toFixed(2)}</span>
                      </div>
                    )}
                    {selectedItem.include_digital_membership && !selectedItem.fee_breakdown.digital_membership_fee && !selectedItem.fee_breakdown.dm_price && (
                      <div className="flex justify-between text-purple-400">
                        <span>✓ Includes Digital Membership</span>
                        <span></span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold pt-2 border-t">
                      <span>Total</span>
                      <span>${selectedItem.amount?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Children enrolled (fallback if no child_breakdowns) */}
              {selectedItem.children_ids?.length > 0 && !selectedItem.fee_breakdown?.child_breakdowns && (
                <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                  <p className="text-sm text-orange-400 font-medium">👶 Children Being Registered</p>
                  <p className="text-lg font-bold text-orange-300">{selectedItem.children_ids.length} child(ren)</p>
                </div>
              )}

              {selectedItem.bank_account_holder_name && (
                <div className="p-3 bg-emerald-500/10 border border-green-500/30 rounded-lg">
                  <p className="text-sm text-emerald-400 font-medium">🏦 Bank Account Name</p>
                  <p className="text-lg font-bold text-green-300">{selectedItem.bank_account_holder_name}</p>
                </div>
              )}

              <div className="text-xs text-slate-400">
                <p>Payment Method: {selectedItem.payment_method || 'N/A'}</p>
                <p>Reference: {selectedItem.payment_reference}</p>
                <p>Created: {formatDate(selectedItem.created_at)}</p>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setSelectedItem(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Member Details Dialog */}
      <Dialog open={!!selectedMember} onOpenChange={() => setSelectedMember(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Member Details</DialogTitle>
            <DialogDescription>Full details for {selectedMember?.name}</DialogDescription>
          </DialogHeader>
          {selectedMember && (
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-400">Full Name</p>
                  <p className="font-medium">{selectedMember.name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Email</p>
                  <p className="font-medium">{selectedMember.email}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Phone</p>
                  <p className="font-medium">{selectedMember.phone || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Registered</p>
                  <p className="font-medium">{formatDate(selectedMember.created_at)}</p>
                </div>
              </div>

              {/* Membership Status */}
              <div className="p-3 bg-[#0a0f1a] rounded-lg">
                <p className="text-sm font-medium text-slate-300 mb-2">📋 Membership Status</p>
                <div className="flex flex-wrap gap-2">
                  {selectedMember.is_digital_member ? (
                    <Badge className="bg-purple-500/20 text-purple-400">✓ Digital Member</Badge>
                  ) : (
                    <Badge variant="outline" className="text-slate-400">No Digital Membership</Badge>
                  )}
                  {selectedMember.is_scc_member && (
                    <Badge className="bg-emerald-500/20 text-emerald-400">✓ SCC Member</Badge>
                  )}
                  {selectedMember.member_type === 'section' && (
                    <Badge className="bg-blue-500/20 text-blue-400">✓ Section Member</Badge>
                  )}
                </div>
                {selectedMember.digital_membership_number && (
                  <p className="text-sm mt-2">DM Number: <strong>{selectedMember.digital_membership_number}</strong></p>
                )}
                {selectedMember.digital_membership_expires_at && (
                  <p className="text-sm">DM Expires: <strong>{formatDate(selectedMember.digital_membership_expires_at)}</strong></p>
                )}
              </div>

              {/* Roles */}
              <div className="p-3 bg-blue-500/10 rounded-lg">
                <p className="text-sm font-medium text-blue-400 mb-2">👤 Roles & Registration</p>
                <div className="flex flex-wrap gap-2">
                  {selectedMember.roles?.map((role, idx) => (
                    <Badge key={idx} className="bg-blue-500/20 text-blue-400 capitalize">
                      {role.replace(/_/g, ' ')}
                    </Badge>
                  ))}
                </div>
                {selectedMember.academy_parent_children?.length > 0 && (
                  <p className="text-sm mt-2">Children registered: <strong>{selectedMember.academy_parent_children.length}</strong></p>
                )}
              </div>

              {/* What they're paying for - if there's pending payment */}
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-sm font-medium text-yellow-400 mb-2">💳 Pending Authorization For</p>
                <ul className="text-sm space-y-1">
                  {selectedMember.roles?.includes('academy_parent') && (
                    <li>• Academy Parent Registration</li>
                  )}
                  {selectedMember.is_digital_member && (
                    <li>• Digital Membership Purchase</li>
                  )}
                  {selectedMember.academy_parent_children?.length > 0 && (
                    <li>• {selectedMember.academy_parent_children.length} child(ren) enrollment</li>
                  )}
                </ul>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedMember(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={() => { setConfirmDialog({ open: false, action: null, item: null, type: null }); setRejectionReason(""); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{confirmDialog.action === "approve" ? "Confirm Approval" : "Confirm Rejection"}</DialogTitle>
            <DialogDescription>
              {confirmDialog.action === "approve" ? "Are you sure you want to approve this item?" : "Are you sure you want to reject this item?"}
            </DialogDescription>
          </DialogHeader>
          
          {/* Rejection Reason Input - Show only for rejections */}
          {confirmDialog.action === "reject" && confirmDialog.type === "registration" && (
            <div className="py-4">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Rejection Reason <span className="text-slate-400">(will be sent to parent)</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="e.g., Invalid membership number provided. Please register as a non-member or provide a valid SCC membership number."
                className="w-full p-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder:text-slate-400 focus:border-yellow-500/50 focus:outline-none min-h-[100px]"
              />
              <p className="text-xs text-slate-400 mt-2">
                Common reasons: Invalid membership number, Incomplete information, Payment not received
              </p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirmDialog({ open: false, action: null, item: null, type: null }); setRejectionReason(""); }}>Cancel</Button>
            <Button
              className={confirmDialog.action === "approve" ? "bg-green-600 hover:bg-green-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"}
              disabled={actionLoading}
              onClick={() => {
                const { item, action, type } = confirmDialog;
                switch (type) {
                  case "payment": handlePaymentVerify(item.id, action); break;
                  case "registration": handleRegistrationApprove(item.id, action); break;
                  case "member": handleMemberAuthorize(item.id, action); break;
                  case "joinRequest": handleJoinRequest(item.id, action); break;
                  case "merchant": handleMerchantApplication(item.id, action); break;
                  case "coach": handleCoachApplication(item.id, action); break;
                  case "mc": handleMCApproval(item.id, action); break;
                  default: break;
                }
              }}
            >
              {actionLoading ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : (confirmDialog.action === "approve" ? "Confirm Approval" : "Confirm Rejection")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
